/**
 * Vouchers list page with filtering, sorting, search, and pagination
 */
import React, { useEffect, useState } from 'react';
import {
  Search,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useVoucherStore } from '@/store/voucherStore';
import { Loading } from '@/components/Loading';
import { CopyButton } from '@/components/CopyButton';
import { formatCurrency, formatDate, isExpired, isExpiringSoon } from '@/utils/dateUtils';
import { VoucherStatus } from '@/types';
import type { VoucherFilter } from '@/types';
import { toast } from 'react-toastify';
import { apiService } from '@/services/api';

export const VouchersList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    vouchers,
    filters,
    pagination,
    isLoading,
    fetchVouchers,
    setFilters,
    setPagination,
    deleteVoucher,
    markVoucherUsed,
  } = useVoucherStore();

  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<string | null>(null);
  const [selectedVouchers, setSelectedVouchers] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkUseDialogOpen, setBulkUseDialogOpen] = useState(false);
  const [brands, setBrands] = useState<string[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setFilters({ status: statusParam as VoucherStatus });
    } else {
      setFilters({ status: VoucherStatus.ACTIVE });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSelectedVouchers(new Set());
  }, [vouchers]);

  useEffect(() => {
    const loadBrands = async () => {
      try {
        setLoadingBrands(true);
        const brandsData = await apiService.getBrands();
        setBrands(brandsData);
      } catch (error) {
        console.error('Error loading brands:', error);
      } finally {
        setLoadingBrands(false);
      }
    };
    loadBrands();
  }, []);

  useEffect(() => {
    fetchVouchers();
  }, [filters, pagination.page, pagination.pageSize, fetchVouchers]);

  const handleSearch = () => {
    setFilters({ search: searchTerm || undefined });
  };

  const handleFilterChange = (key: keyof VoucherFilter, value: string | undefined) => {
    setFilters({ [key]: value || undefined });
  };

  const handleSortChange = (sortBy: VoucherFilter['sort_by'], sortOrder: VoucherFilter['sort_order']) => {
    setFilters({ sort_by: sortBy, sort_order: sortOrder });
  };

  const handleDeleteClick = (id: string) => {
    setSelectedVoucher(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedVoucher) {
      try {
        await deleteVoucher(selectedVoucher);
        toast.success('Voucher deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedVoucher(null);
      } catch {
        toast.error('Failed to delete voucher');
      }
    }
  };

  const handleUseClick = (id: string) => {
    setSelectedVoucher(id);
    setUseDialogOpen(true);
  };

  const handleUseConfirm = async () => {
    if (selectedVoucher) {
      try {
        await markVoucherUsed(selectedVoucher);
        toast.success('Voucher marked as used');
        setUseDialogOpen(false);
        setSelectedVoucher(null);
      } catch {
        toast.error('Failed to mark voucher as used');
      }
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedVouchers(new Set(vouchers.map((v) => v.id)));
    } else {
      setSelectedVouchers(new Set());
    }
  };

  const handleSelectOne = (voucherId: string) => {
    const newSelected = new Set(selectedVouchers);
    if (newSelected.has(voucherId)) {
      newSelected.delete(voucherId);
    } else {
      newSelected.add(voucherId);
    }
    setSelectedVouchers(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedVouchers.size === 0) return;
    try {
      const result = await apiService.bulkDeleteVouchers(Array.from(selectedVouchers));
      toast.success(`Deleted ${result.deleted_count} voucher(s)`);
      setBulkDeleteDialogOpen(false);
      setSelectedVouchers(new Set());
      fetchVouchers();
    } catch {
      toast.error('Failed to delete vouchers');
    }
  };

  const handleBulkMarkUsed = async () => {
    if (selectedVouchers.size === 0) return;
    try {
      const result = await apiService.bulkMarkUsed(Array.from(selectedVouchers));
      toast.success(`Marked ${result.updated_count} voucher(s) as used`);
      setBulkUseDialogOpen(false);
      setSelectedVouchers(new Set());
      fetchVouchers();
    } catch {
      toast.error('Failed to mark vouchers as used');
    }
  };

  const getStatusVariant = (status: VoucherStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case VoucherStatus.ACTIVE:
        return 'default';
      case VoucherStatus.USED:
        return 'secondary';
      case VoucherStatus.EXPIRED:
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getExpiryWarning = (voucher: (typeof vouchers)[0]) => {
    if (isExpired(voucher.expiry_date)) {
      return { icon: <XCircle className="size-4 text-destructive" />, label: 'Expired' };
    }
    if (isExpiringSoon(voucher.expiry_date)) {
      return { icon: <AlertTriangle className="size-4 text-warning" />, label: 'Expiring Soon' };
    }
    return null;
  };

  if (isLoading && vouchers.length === 0) {
    return <Loading message="Loading vouchers..." />;
  }

  const allSelected = vouchers.length > 0 && selectedVouchers.size === vouchers.length;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Vouchers</h1>
        <Button onClick={() => navigate('/upload')}>Upload Voucher</Button>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex min-w-[200px] flex-1 items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button variant="secondary" size="icon" onClick={handleSearch} aria-label="Search">
                <Search className="size-4" />
              </Button>
            </div>
            <div className="min-w-[150px] space-y-2">
              <Label>Status</Label>
              <Select
                value={filters.status ?? '__all__'}
                onValueChange={(v) => handleFilterChange('status', v === '__all__' ? undefined : v)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value={VoucherStatus.ACTIVE}>Active</SelectItem>
                  <SelectItem value={VoucherStatus.USED}>Used</SelectItem>
                  <SelectItem value={VoucherStatus.EXPIRED}>Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px] space-y-2">
              <Label>Platform</Label>
              <Select
                value={filters.source_platform ?? '__all__'}
                onValueChange={(v) => handleFilterChange('source_platform', v === '__all__' ? undefined : v)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  <SelectItem value="Gyftr">Gyftr</SelectItem>
                  <SelectItem value="iShop">iShop</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px] space-y-2">
              <Label>Brand</Label>
              <Select
                value={filters.brand_name ?? '__all__'}
                onValueChange={(v) => handleFilterChange('brand_name', v === '__all__' ? undefined : v)}
                disabled={loadingBrands}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand} value={brand}>
                      {brand}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[170px] space-y-2">
              <Label>Sort By</Label>
              <Select
                value={filters.sort_by || 'expiry_date'}
                onValueChange={(v) =>
                  handleSortChange(v as VoucherFilter['sort_by'], filters.sort_order || 'asc')
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expiry_date">Expiry Date</SelectItem>
                  <SelectItem value="value">Value</SelectItem>
                  <SelectItem value="brand_name">Brand</SelectItem>
                  <SelectItem value="purchase_date">Purchase Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[150px] space-y-2">
              <Label>Order</Label>
              <Select
                value={filters.sort_order || 'asc'}
                onValueChange={(v) =>
                  handleSortChange(filters.sort_by || 'expiry_date', v as VoucherFilter['sort_order'])
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedVouchers.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3">
          <span className="text-sm font-medium">
            {selectedVouchers.size} voucher(s) selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10"
              onClick={() => setBulkDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-1 size-4" />
              Delete
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkUseDialogOpen(true)}
            >
              <CheckCircle className="mr-1 size-4" />
              Mark as Used
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(c) => handleSelectAll(c === true)}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>Purchase Date</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Coupon Code</TableHead>
              <TableHead>PIN</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vouchers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">
                  No vouchers found
                </TableCell>
              </TableRow>
            ) : (
              vouchers.map((voucher) => {
                const expiryWarning = getExpiryWarning(voucher);
                return (
                  <TableRow key={voucher.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedVouchers.has(voucher.id)}
                        onCheckedChange={() => handleSelectOne(voucher.id)}
                        aria-label={`Select ${voucher.brand_name}`}
                      />
                    </TableCell>
                    <TableCell>
                      {voucher.purchase_date ? formatDate(voucher.purchase_date) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{voucher.brand_name}</span>
                        {expiryWarning?.icon}
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(voucher.value)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span>{voucher.coupon_code || '—'}</span>
                        {voucher.coupon_code && (
                          <CopyButton
                            text={voucher.coupon_code}
                            label="Code"
                            size="small"
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span>{voucher.pin || '—'}</span>
                        {voucher.pin && (
                          <CopyButton text={voucher.pin} label="PIN" size="small" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{voucher.source_platform}</TableCell>
                    <TableCell>
                      {voucher.expiry_date ? formatDate(voucher.expiry_date) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(voucher.status)}>{voucher.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => navigate(`/vouchers/${voucher.id}`)}
                          aria-label="View details"
                        >
                          <Eye className="size-4" />
                        </Button>
                        {voucher.claim_link && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() =>
                              window.open(voucher.claim_link!, '_blank', 'noopener,noreferrer')
                            }
                            aria-label="Open claim link"
                          >
                            <ExternalLink className="size-4" />
                          </Button>
                        )}
                        {voucher.status === VoucherStatus.ACTIVE && (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-success"
                            onClick={() => handleUseClick(voucher.id)}
                            aria-label="Mark as used"
                          >
                            <CheckCircle className="size-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive"
                          onClick={() => handleDeleteClick(voucher.id)}
                          aria-label="Delete"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              Page {pagination.page} of {Math.max(1, Math.ceil(pagination.total / pagination.pageSize))}
            </span>
            <Select
              value={String(pagination.pageSize)}
              onValueChange={(v) => setPagination(1, parseInt(v, 10))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[10, 25, 50, 100].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span>per page</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(pagination.page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
              onClick={() => setPagination(pagination.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voucher</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this voucher? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={useDialogOpen} onOpenChange={setUseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Voucher as Used</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this voucher as used?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUseConfirm}>Mark as Used</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vouchers</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedVouchers.size} voucher(s)? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleBulkDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkUseDialogOpen} onOpenChange={setBulkUseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Vouchers as Used</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark {selectedVouchers.size} voucher(s) as used?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkMarkUsed}>Mark as Used</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
